import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core'

import { CommonModule } from '@angular/common'
import { flashMessages } from '../../../../locale/flash-messages/flash-messages.en'
import { HTMLInputEvent } from '../../interfaces/html-input-event.interface'
import { SelectOption } from '../../interfaces/select-option.interface'
import { StripHtmlPipe } from '../../pipes/strip-html.pipe'
import { FlashMessageService } from '../../services/flash-message.service'

@Component({
  selector: 'app-multi-tags-input',
  templateUrl: './multi-tags-input.component.html',
  styleUrls: ['./multi-tags-input.component.scss'],
  standalone: true,
  imports: [CommonModule, StripHtmlPipe]
})
export class MultiTagsInputComponent implements OnChanges {
  @Input() values: SelectOption[]
  @Input() initialValues: number[]
  @Input() maxSelectedItems?: number = 99
  @Input() placeholder: string

  @Input() addNewOptionFunction?: (
    newOptionName: string
  ) => Promise<SelectOption[]>
  @Output() selectedValuesChanged: EventEmitter<(number | string)[]> =
    new EventEmitter()

  @ViewChild('searchInput', { static: true }) searchInputEl: ElementRef

  selectedValues: SelectOption[] = []
  suggestedValues: SelectOption[] = []

  terms = ''
  selectedTags = []
  showList = false
  focusedValueIndex: number

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private flashMessageService: FlashMessageService
  ) {}

  ngOnChanges(): void {
    if (this.terms.length === 0) {
      this.values.sort((a, b) => a.label.localeCompare(b.label))
    }

    this.suggestedValues = this.values

    if (this.initialValues) {
      this.selectedValues = this.initialValues.map((iV: number) =>
        this.values.find((v) => v.value === iV)
      )
    } else {
      this.selectedValues = []
    }
  }

  toggleValue(value: SelectOption): void {
    if (this.selectedValues && this.selectedValues.includes(value)) {
      this.selectedValues.splice(this.selectedValues.indexOf(value), 1)
    } else if (
      !this.maxSelectedItems ||
      this.selectedValues.length < this.maxSelectedItems
    ) {
      this.selectedValues.push(value)
      this.renderer.setProperty(this.searchInputEl.nativeElement, 'value', '')
    }

    this.showList = false
    this.selectedValuesChanged.emit(this.selectedValues.map((v) => v.value))
  }

  onSearchInputKeyup(event: HTMLInputEvent) {
    // Remove last selected value on backspace click
    if (
      event.key === 'Backspace' &&
      !this.terms &&
      this.selectedValues.length
    ) {
      this.selectedValues.splice(-1, 1)
    }

    this.terms = event.target.value.toLowerCase()
    this.showList = true

    // Navigate through results
    if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
      return this.navigateSuggestedValues(event.key)
    }

    // Filter results
    this.suggestedValues = this.values.filter((v: SelectOption) =>
      v.label.toLowerCase().includes(this.terms)
    )
  }

  addNewOption(name: string) {
    this.addNewOptionFunction(name)
      .then((termsCreated: SelectOption[]) => {
        termsCreated.forEach((termCreated) => {
          this.suggestedValues.push(termCreated)
          this.selectedValues.push(termCreated)
        })

        this.selectedValuesChanged.emit(this.selectedValues.map((v) => v.value))
        this.renderer.setProperty(this.searchInputEl.nativeElement, 'value', '')
        this.showList = false
      })
      .catch(() => {
        this.flashMessageService.error(flashMessages.tool.error.cannotSend)
      })
  }

  isAlreadySelected(term: SelectOption): boolean {
    return !!this.selectedValues.find((v) => v.value === term.value)
  }

  // Use arrowKeys and enter to select suggested themes with keyboard
  navigateSuggestedValues(key: string): void {
    if (key === 'ArrowDown') {
      if (typeof this.focusedValueIndex === 'undefined') {
        this.showList = true
        this.focusedValueIndex = 0
      } else if (this.focusedValueIndex < this.suggestedValues.length) {
        this.focusedValueIndex++
      }
    } else if (key === 'ArrowUp') {
      if (!this.focusedValueIndex) {
        this.showList = false
        delete this.focusedValueIndex
      } else {
        this.focusedValueIndex--
      }
    } else if (
      key === 'Enter' &&
      typeof this.focusedValueIndex !== 'undefined' &&
      this.suggestedValues[this.focusedValueIndex]
    ) {
      this.toggleValue(this.suggestedValues[this.focusedValueIndex])
      delete this.focusedValueIndex
    }
  }

  // Click outside closes list.
  @HostListener('document:click', ['$event.target'])
  clickOut(eventTarget) {
    if (!this.elementRef.nativeElement.contains(eventTarget)) {
      this.showList = false
    }
  }
}
